import {
  ageOn,
  COMPANY,
  DECLARED_LEVEL_LABELS,
  DECLARED_LEVELS,
  type DeclaredLevel,
  isFullName,
  isValidBrMobile,
  isValidCep,
  isValidCpf,
  isValidEmail,
  UFS,
} from "@byfluent/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { PublicLayout } from "../../components/PanelLayout";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Chips,
  CopyButton,
  LinkButton,
  Loading,
  SelectInput,
  TextInput,
} from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { brl, whatsappUrl } from "../../lib/format";

interface Payment {
  subscriptionId: string;
  method: "CREDIT_CARD" | "PIX" | "BOLETO";
  status: "pending" | "paid";
  valueCents: number;
  pix: { payload: string; encodedImage: string; expirationDate: string } | null;
  boletoLine: string | null;
  invoiceUrl: string | null;
  hasCheckout: boolean;
  devPayUrl: string | null;
}

type Summary =
  | { status: "expired"; supportWhatsapp: string }
  | {
      status: string;
      purpose: "primary" | "second_student";
      expiresLabel: string;
      plan: {
        code: string;
        name: string;
        isDuo: boolean;
        aiMinutes: number;
        weeklyMinutes: number;
      };
      singlePayer: boolean;
      price: { totalCents: number; lessonsCents: number; platformCents: number };
      teacher: {
        name: string;
        companyName: string | null;
        cnpj: string | null;
        photoUrl: string | null;
      } | null;
      slotLabel: string;
      students: {
        id: string;
        position: number;
        name: string;
        email: string | null;
        filled: boolean;
      }[];
      linkStudentName: string | null;
      boletoEnabled: boolean;
      payment: Payment | null;
      supportWhatsapp: string;
    };

type Ready = Exclude<Summary, { status: "expired" }>;

interface StudentForm {
  name: string;
  birthDate: string;
  level: DeclaredLevel;
  email: string;
  phone: string;
}

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
const emptyStudent = (name = ""): StudentForm => ({
  name,
  birthDate: "",
  level: "beginner",
  email: "",
  phone: "",
});

function studentErrors(s: StudentForm): Record<string, string> {
  const e: Record<string, string> = {};
  if (!isFullName(s.name)) e.name = "Informe nome e sobrenome.";
  if (!s.birthDate) e.birthDate = "Informe a data de nascimento.";
  else {
    const age = ageOn(s.birthDate, today());
    if (age < 6 || age > 100) e.birthDate = "A idade precisa estar entre 6 e 100 anos.";
  }
  if (!isValidEmail(s.email)) e.email = "E-mail inválido.";
  if (!isValidBrMobile(s.phone)) e.phone = "Celular brasileiro com DDD.";
  return e;
}

export function EnrollmentPage() {
  const { token = "" } = useParams();
  const [params] = useSearchParams();
  const q = useQuery({
    queryKey: ["enrollment-link", token],
    queryFn: () => api.get<Summary>(`/v1/enrollment-links/${token}`),
    retry: false,
    // "Aguardando pagamento": consulta a cada 5 s até ativar.
    refetchInterval: (query) => {
      const d = query.state.data;
      return d && d.status !== "expired" && d.status !== "active" && "payment" in d && d.payment
        ? 5000
        : false;
    },
  });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) {
    return (
      <PublicLayout>
        <Alert kind="risk">{errorMessage(q.error)}</Alert>
      </PublicLayout>
    );
  }
  if (q.data.status === "expired") {
    const support = (q.data as Extract<Summary, { status: "expired" }>).supportWhatsapp;
    return (
      <PublicLayout support={support}>
        <Card>
          <h1>Este link venceu</h1>
          <p style={{ margin: "12px 0" }}>
            A reserva do horário dura 48 horas. Fale com a vendedora para gerar um novo link.
          </p>
          <LinkButton
            variant="whatsapp"
            href={whatsappUrl(support)}
            target="_blank"
            rel="noreferrer"
          >
            Falar no WhatsApp
          </LinkButton>
        </Card>
      </PublicLayout>
    );
  }
  const d = q.data as Ready;
  if (d.status === "active" || d.status === "paid") {
    return (
      <PublicLayout support={d.supportWhatsapp}>
        <Card>
          <h1>Matrícula confirmada!</h1>
          <div className="brand-stripe" aria-hidden>
            <span />
            <span />
            <span />
            <span />
          </div>
          <p style={{ marginTop: 12 }}>
            Bem-vindo à ByFluent. Seu professor é <strong>{d.teacher?.name}</strong>, no horário{" "}
            {d.slotLabel}. Ele vai chamar você no WhatsApp em até 1 dia útil. Enviamos os detalhes
            por e-mail.
          </p>
        </Card>
      </PublicLayout>
    );
  }
  return (
    <PublicLayout support={d.supportWhatsapp}>
      <SummaryCard d={d} />
      {d.payment && d.payment.status === "pending" && params.get("trocar") !== "1" ? (
        <PaymentCard payment={d.payment} token={token} cardReturn={params.get("pagamento")} />
      ) : (
        <EnrollmentForm d={d} token={token} />
      )}
    </PublicLayout>
  );
}

function SummaryCard({ d }: { d: Ready }) {
  return (
    <Card>
      <h1>{d.plan.name}</h1>
      <div className="brand-stripe" aria-hidden>
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="stack" style={{ marginTop: 12 }}>
        <div className="row between">
          <span>Mensalidade{d.plan.isDuo && d.singlePayer ? " (dois alunos)" : ""}</span>
          <strong>{brl(d.price.totalCents)}</strong>
        </div>
        <div className="row between small">
          <span>Aulas com o professor</span>
          <span>{brl(d.price.lessonsCents)}</span>
        </div>
        <div className="row between small">
          <span>Plataforma ByFluent</span>
          <span>{brl(d.price.platformCents)}</span>
        </div>
        <div className="small muted">
          Professor: <strong>{d.teacher?.name}</strong>
          {d.teacher?.companyName
            ? ` (${d.teacher.companyName}${d.teacher.cnpj ? `, CNPJ ${d.teacher.cnpj}` : ""})`
            : ""}
          <br />
          Plataforma: {COMPANY.brand} ({COMPANY.legalName}, CNPJ {COMPANY.cnpj})
          <br />
          Horário semanal reservado: {d.slotLabel}
          <br />
          {d.plan.aiMinutes} min por mês de conversação com IA · sem fidelidade · garantia de 7 dias
          <br />
          Reserva válida até {d.expiresLabel}
        </div>
      </div>
    </Card>
  );
}

function PaymentCard({
  payment,
  token,
  cardReturn,
}: {
  payment: Payment;
  token: string;
  cardReturn: string | null;
}) {
  const qc = useQueryClient();
  const devPay = useMutation({
    mutationFn: async () => {
      await fetch(payment.devPayUrl ?? "", { method: "POST" });
    },
    onSuccess: () =>
      setTimeout(() => qc.invalidateQueries({ queryKey: ["enrollment-link", token] }), 800),
  });
  return (
    <Card title="Pagamento">
      <div className="stack">
        <Alert kind="note" title="Aguardando pagamento">
          Assim que a Asaas confirmar, esta página muda sozinha.
        </Alert>
        {payment.method === "PIX" && payment.pix ? (
          <>
            <img
              className="qr"
              src={`data:image/png;base64,${payment.pix.encodedImage}`}
              alt="QR Code do Pix"
            />
            <p className="small muted">Pix copia e cola:</p>
            <div className="copy-box">{payment.pix.payload}</div>
            <CopyButton text={payment.pix.payload} label="Copiar código Pix" />
            <p className="small muted">As próximas mensalidades chegam por e-mail da Asaas.</p>
          </>
        ) : null}
        {payment.method === "BOLETO" && payment.boletoLine ? (
          <>
            <div className="copy-box">{payment.boletoLine}</div>
            <CopyButton text={payment.boletoLine} label="Copiar linha digitável" />
          </>
        ) : null}
        {payment.method === "CREDIT_CARD" ? (
          cardReturn === "sucesso" ? (
            <p>Recebemos seu cartão. Estamos aguardando a confirmação da Asaas.</p>
          ) : (
            <p>
              Abra o checkout seguro da Asaas para pagar com cartão. O cartão nunca passa pela
              ByFluent.
            </p>
          )
        ) : null}
        {payment.invoiceUrl ? (
          <LinkButton
            variant="secondary"
            href={payment.invoiceUrl}
            target="_blank"
            rel="noreferrer"
          >
            Abrir fatura
          </LinkButton>
        ) : null}
        {payment.devPayUrl ? (
          <Button variant="ghost" loading={devPay.isPending} onClick={() => devPay.mutate()}>
            Simular pagamento (ambiente de teste)
          </Button>
        ) : null}
        <a href={`/m/${token}?trocar=1`} className="small">
          Trocar a forma de pagamento
        </a>
      </div>
    </Card>
  );
}

function EnrollmentForm({ d, token }: { d: Ready; token: string }) {
  const qc = useQueryClient();
  const linkStudent = d.students.find((s) => s.name === d.linkStudentName) ?? d.students[0];
  const studentKnown = d.purpose === "second_student" && Boolean(linkStudent?.filled);
  const isDuoPrimary = d.plan.isDuo && d.purpose === "primary";
  const steps = [
    "student",
    "contractor",
    ...(isDuoPrimary ? ["duo"] : []),
    "consents",
    "payment",
  ].filter((s) => !(studentKnown && s === "student"));
  const [step, setStep] = useState(0);
  const [student, setStudent] = useState<StudentForm>(emptyStudent(linkStudent?.name ?? ""));
  const [second, setSecond] = useState<StudentForm>(
    emptyStudent(d.students.find((s) => s.position === 2)?.name ?? ""),
  );
  const [cpf, setCpf] = useState("");
  const [guardian, setGuardian] = useState({ name: "", cpf: "", email: "", phone: "" });
  const [address, setAddress] = useState({
    cep: "",
    street: "",
    number: "",
    complement: "",
    district: "",
    city: "",
    uf: "SP",
  });
  const [consents, setConsents] = useState({
    lessonsContract: false,
    platformTerms: false,
    privacy: false,
    contact: false,
  });
  const [method, setMethod] = useState<"CREDIT_CARD" | "PIX" | "BOLETO">("PIX");
  const [touched, setTouched] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const current = steps[step];
  const minor = !studentKnown && student.birthDate ? ageOn(student.birthDate, today()) < 18 : false;

  const fill = useMutation({
    mutationFn: () =>
      api.post<{ payment: { method: string; checkoutUrl?: string } }>(
        `/v1/enrollment-links/${token}/fill`,
        {
          student: studentKnown ? undefined : student,
          secondStudent: isDuoPrimary ? second : undefined,
          guardian: minor ? guardian : undefined,
          contractorCpf: minor ? undefined : cpf,
          address: { ...address, complement: address.complement || undefined },
          consents,
          paymentMethod: method,
        },
      ),
    onSuccess: (r) => {
      if (r.payment.method === "CREDIT_CARD" && r.payment.checkoutUrl) {
        window.location.href = r.payment.checkoutUrl;
        return;
      }
      window.history.replaceState(null, "", `/m/${token}`);
      qc.invalidateQueries({ queryKey: ["enrollment-link", token] });
    },
  });

  const lookupCep = async (cep: string) => {
    const digits = cep.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const j = (await r.json()) as {
        logradouro?: string;
        bairro?: string;
        localidade?: string;
        uf?: string;
        erro?: boolean;
      };
      if (!j.erro)
        setAddress((a) => ({
          ...a,
          street: j.logradouro || a.street,
          district: j.bairro || a.district,
          city: j.localidade || a.city,
          uf: j.uf || a.uf,
        }));
    } catch {
      // O aluno preenche à mão.
    } finally {
      setCepLoading(false);
    }
  };

  const errors = (): Record<string, string> => {
    if (current === "student") return studentErrors(student);
    if (current === "duo") {
      const e = studentErrors(second);
      if (
        !d.singlePayer &&
        second.email &&
        second.email.toLowerCase() === student.email.toLowerCase()
      )
        e.email = "Use um e-mail diferente do primeiro aluno.";
      return e;
    }
    if (current === "contractor") {
      const e: Record<string, string> = {};
      if (minor) {
        if (!isFullName(guardian.name)) e.gname = "Informe nome e sobrenome do responsável.";
        if (!isValidCpf(guardian.cpf)) e.gcpf = "CPF inválido.";
        if (!isValidEmail(guardian.email)) e.gemail = "E-mail inválido.";
        if (!isValidBrMobile(guardian.phone)) e.gphone = "Celular com DDD.";
      } else if (!isValidCpf(cpf)) e.cpf = "CPF inválido.";
      if (!isValidCep(address.cep)) e.cep = "CEP com 8 dígitos.";
      if (!address.street) e.street = "Informe a rua.";
      if (!address.number) e.number = "Informe o número.";
      if (!address.district) e.district = "Informe o bairro.";
      if (!address.city) e.city = "Informe a cidade.";
      return e;
    }
    if (current === "consents" && !Object.values(consents).every(Boolean))
      return { consents: "Aceite todos os itens para continuar." };
    return {};
  };
  const errs = touched ? errors() : {};
  const next = () => {
    setTouched(true);
    if (Object.keys(errors()).length > 0) return;
    setTouched(false);
    if (step < steps.length - 1) setStep(step + 1);
    else fill.mutate();
  };

  const STEP_TITLES: Record<string, string> = {
    student: "Quem estuda",
    contractor: minor ? "Responsável e endereço" : "Contratante e endereço",
    duo: "Segundo aluno da dupla",
    consents: "Aceites",
    payment: "Pagamento",
  };

  return (
    <Card>
      <div className="steps" style={{ marginBottom: 12 }}>
        {steps.map((s, i) => (
          <span key={s} className={i <= step ? "done" : ""} />
        ))}
      </div>
      <h2>
        {step + 1}. {STEP_TITLES[current ?? ""]}
      </h2>
      <div className="stack" style={{ marginTop: 12 }}>
        {studentKnown && step === 0 ? (
          <Alert kind="note">Matrícula de {linkStudent?.name}, iniciada pela dupla.</Alert>
        ) : null}
        {current === "student" || current === "duo" ? (
          <StudentFields
            value={current === "student" ? student : second}
            onChange={current === "student" ? setStudent : setSecond}
            errors={errs}
          />
        ) : null}
        {current === "contractor" ? (
          <>
            {minor ? (
              <>
                <Alert kind="note">
                  Quem estuda tem menos de 18 anos: o responsável é o contratante e o pagador.
                </Alert>
                <TextInput
                  label="Nome do responsável"
                  value={guardian.name}
                  onChange={(e) => setGuardian({ ...guardian, name: e.target.value })}
                  error={errs.gname}
                />
                <TextInput
                  label="CPF do responsável"
                  inputMode="numeric"
                  value={guardian.cpf}
                  onChange={(e) => setGuardian({ ...guardian, cpf: e.target.value })}
                  error={errs.gcpf}
                />
                <TextInput
                  label="E-mail do responsável"
                  type="email"
                  value={guardian.email}
                  onChange={(e) => setGuardian({ ...guardian, email: e.target.value })}
                  error={errs.gemail}
                />
                <TextInput
                  label="Celular do responsável"
                  inputMode="tel"
                  value={guardian.phone}
                  onChange={(e) => setGuardian({ ...guardian, phone: e.target.value })}
                  error={errs.gphone}
                />
              </>
            ) : (
              <TextInput
                label="Seu CPF"
                inputMode="numeric"
                value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                error={errs.cpf}
              />
            )}
            <TextInput
              label="CEP"
              inputMode="numeric"
              value={address.cep}
              hint={cepLoading ? "Buscando endereço" : undefined}
              onChange={(e) => {
                setAddress({ ...address, cep: e.target.value });
                void lookupCep(e.target.value);
              }}
              error={errs.cep}
            />
            <TextInput
              label="Rua"
              value={address.street}
              onChange={(e) => setAddress({ ...address, street: e.target.value })}
              error={errs.street}
            />
            <div className="grid cols-2" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <TextInput
                label="Número"
                value={address.number}
                onChange={(e) => setAddress({ ...address, number: e.target.value })}
                error={errs.number}
              />
              <TextInput
                label="Complemento"
                value={address.complement}
                onChange={(e) => setAddress({ ...address, complement: e.target.value })}
              />
            </div>
            <TextInput
              label="Bairro"
              value={address.district}
              onChange={(e) => setAddress({ ...address, district: e.target.value })}
              error={errs.district}
            />
            <div className="grid cols-2" style={{ gridTemplateColumns: "2fr 1fr" }}>
              <TextInput
                label="Cidade"
                value={address.city}
                onChange={(e) => setAddress({ ...address, city: e.target.value })}
                error={errs.city}
              />
              <SelectInput
                label="UF"
                value={address.uf}
                onChange={(e) => setAddress({ ...address, uf: e.target.value })}
                options={UFS.map((u) => ({ value: u, label: u }))}
              />
            </div>
          </>
        ) : null}
        {current === "consents" ? (
          <>
            <Checkbox
              checked={consents.lessonsContract}
              onChange={(e) => setConsents({ ...consents, lessonsContract: e.target.checked })}
              label={
                <>
                  Li e aceito o{" "}
                  <a
                    href="https://byfluent.com.br/contrato-de-aulas"
                    target="_blank"
                    rel="noreferrer"
                  >
                    contrato de prestação de aulas
                  </a>{" "}
                  com {d.teacher?.companyName ?? d.teacher?.name}
                  {d.teacher?.cnpj ? ` (CNPJ ${d.teacher.cnpj})` : ""}.
                </>
              }
            />
            <Checkbox
              checked={consents.platformTerms}
              onChange={(e) => setConsents({ ...consents, platformTerms: e.target.checked })}
              label={
                <>
                  Li e aceito os{" "}
                  <a href="https://byfluent.com.br/termos" target="_blank" rel="noreferrer">
                    termos da plataforma ByFluent
                  </a>
                  .
                </>
              }
            />
            <Checkbox
              checked={consents.privacy}
              onChange={(e) => setConsents({ ...consents, privacy: e.target.checked })}
              label={
                <>
                  Li a{" "}
                  <a href="https://byfluent.com.br/privacidade" target="_blank" rel="noreferrer">
                    política de privacidade
                  </a>
                  .
                </>
              }
            />
            <Checkbox
              checked={consents.contact}
              onChange={(e) => setConsents({ ...consents, contact: e.target.checked })}
              label="Aceito o contato da ByFluent e do professor por WhatsApp e e-mail."
            />
            {errs.consents ? <Alert kind="warning">{errs.consents}</Alert> : null}
          </>
        ) : null}
        {current === "payment" ? (
          <>
            <Chips
              value={method}
              onChange={setMethod}
              options={[
                { value: "PIX", label: "Pix" },
                { value: "CREDIT_CARD", label: "Cartão (assinatura mensal)" },
                ...(d.boletoEnabled ? [{ value: "BOLETO" as const, label: "Boleto" }] : []),
              ]}
            />
            <p className="small muted">
              {method === "PIX"
                ? "Você recebe o QR Code da primeira mensalidade agora; as próximas chegam por e-mail da Asaas."
                : method === "CREDIT_CARD"
                  ? "Você vai para o checkout seguro da Asaas. A cobrança se repete todo mês no cartão."
                  : "Você recebe a linha digitável da primeira mensalidade."}
            </p>
            {fill.error ? <Alert kind="risk">{errorMessage(fill.error)}</Alert> : null}
          </>
        ) : null}
        <div className="row">
          {step > 0 ? (
            <Button variant="secondary" onClick={() => setStep(step - 1)}>
              Voltar
            </Button>
          ) : null}
          <Button block={step === 0} loading={fill.isPending} onClick={next} style={{ flex: 1 }}>
            {current === "payment" ? `Pagar ${brl(d.price.totalCents)}` : "Continuar"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function StudentFields({
  value,
  onChange,
  errors,
}: {
  value: StudentForm;
  onChange: (v: StudentForm) => void;
  errors: Record<string, string>;
}) {
  return (
    <>
      <TextInput
        label="Nome completo"
        autoComplete="name"
        value={value.name}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
        error={errors.name}
      />
      <TextInput
        label="Data de nascimento"
        type="date"
        value={value.birthDate}
        onChange={(e) => onChange({ ...value, birthDate: e.target.value })}
        error={errors.birthDate}
      />
      <SelectInput
        label="Nível de inglês"
        value={value.level}
        onChange={(e) => onChange({ ...value, level: e.target.value as DeclaredLevel })}
        options={DECLARED_LEVELS.map((l) => ({ value: l, label: DECLARED_LEVEL_LABELS[l] }))}
      />
      <TextInput
        label="E-mail"
        type="email"
        autoComplete="email"
        value={value.email}
        onChange={(e) => onChange({ ...value, email: e.target.value })}
        error={errors.email}
      />
      <TextInput
        label="WhatsApp"
        inputMode="tel"
        autoComplete="tel"
        value={value.phone}
        onChange={(e) => onChange({ ...value, phone: e.target.value })}
        error={errors.phone}
      />
    </>
  );
}
